import { useGetDropboxAuthorizationUrlQuery } from '@account/services';
import { DropboxFolderType } from '@account/types';
import { getFolderTypeInfo } from '@account/utils/account';
import type { GenericActionDialogButtons } from '@components/display/dialogs';
import { GenericActionDialog } from '@components/display/dialogs';
import { InputField } from '@components/utility/inputs';
import { DialogProps, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type ConnectionScenario = 'create' | 'delete';

type Props = Omit<DialogProps, 'title' | 'content'> & {
    folderType: DropboxFolderType | null;
    scenario: ConnectionScenario;
    onExited?: () => void;
    onCreate?: (payload: { folderType: DropboxFolderType; code: string }) => Promise<unknown>;
    onDelete?: (payload: { folderType: DropboxFolderType }) => Promise<unknown>;
    createLoading?: boolean;
    deleteLoading?: boolean;
};

export const AccountConnectionDialog = (props: Props) => {
    const { folderType, scenario, onExited, onCreate, onDelete, createLoading, deleteLoading, ...dialogProps } = props;
    //#region State
    const [createStep, setCreateStep] = useState<1 | 2>(1);
    const [dropboxCode, setDropboxCode] = useState('');
    //#endregion

    //#region Hooks
    const intl = useIntl();
    //#endregion

    //#region Processed Data
    const viewKey = `${scenario}:${scenario === 'create' ? createStep : 1}`;

    const titleAndIcon = useMemo(() => (folderType ? getFolderTypeInfo(intl, folderType) : null), [folderType, intl]);
    //#endregion

    //#region Queries
    const { data, isLoading: isLoadingAuthorizationUrl } = useGetDropboxAuthorizationUrlQuery(
        { type: folderType ?? DropboxFolderType.Firmwares },
        {
            skip: !folderType,
        }
    );
    //#endregion

    //#region Effects
    useEffect(() => {
        if (!dialogProps.open) return;
        setCreateStep(1);
        setDropboxCode('');
    }, [dialogProps.open, folderType, scenario]);
    //#endregion

    if (!titleAndIcon) return null;

    const { title, icon: Icon } = titleAndIcon;

    //#region Handlers
    const handleDialogClose: NonNullable<DialogProps['onClose']> = (event, reason) => {
        dialogProps.onClose?.(event, reason);
    };

    const handleCreateConfirm = async () => {
        if (!folderType || !onCreate || !dropboxCode.trim()) return;

        await onCreate({
            folderType,
            code: dropboxCode.trim(),
        });

        dialogProps.onClose?.({}, 'escapeKeyDown');
    };

    const handleDeleteConfirm = async () => {
        if (!folderType || !onDelete) return;

        await onDelete({
            folderType,
        });

        dialogProps.onClose?.({}, 'escapeKeyDown');
    };
    //#endregion

    const isCreateFirstStep = scenario === 'create' && createStep === 1;
    const isCreate = scenario === 'create';

    const content = isCreateFirstStep ? (
        <>
            <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600}>
                <FormattedMessage
                    id="account.connections.dialog.step1"
                    defaultMessage="Step 1: Authenticate with Dropbox"
                />
            </Typography>
            <Typography fontSize={14} color="gray-600" mt={1}>
                <FormattedMessage
                    id="account.connections.dialog.step1.description"
                    defaultMessage="You can authenticate with Dropbox below. When the authentication is done you will receive a key. Copy this key, you need it for the next step."
                />
            </Typography>
        </>
    ) : isCreate ? (
        <>
            <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600}>
                <FormattedMessage id="account.connections.dialog.step2" defaultMessage="Step 2: Fill in your key" />
            </Typography>
            <Typography fontSize={14} color="gray-600" mt={1} mb={2}>
                <FormattedMessage
                    id="account.connections.dialog.step2.description"
                    defaultMessage="Fill in the key you have retrieved the previous step. After that you can connect your account."
                />
            </Typography>
            <InputField
                fullWidth
                size="small"
                value={dropboxCode}
                onChange={(event) => setDropboxCode(event.target.value)}
                placeholder={intl.formatMessage({
                    id: 'account.connections.dialog.step2.placeholder',
                    defaultMessage: 'Authentication Key',
                })}
            />
        </>
    ) : (
        <>
            <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600}>
                <FormattedMessage
                    id="account.connections.dialog.delete.title"
                    defaultMessage="Are you sure you want to disconnect?"
                />
            </Typography>
            <Typography fontSize={14} color="gray-600" mt={1}>
                <FormattedMessage
                    id="account.connections.dialog.delete.description"
                    defaultMessage="You will lose access to all shared documents on the Victron Professional platform."
                />
            </Typography>
        </>
    );

    const buttons: GenericActionDialogButtons = isCreateFirstStep
        ? [
              {
                  label: (
                      <FormattedMessage
                          id="account.connections.dialog.authenticate"
                          defaultMessage="Authenticate with Dropbox"
                      />
                  ),
                  buttonProps: {
                      size: 'large',
                      loading: isLoadingAuthorizationUrl,
                      href: data?.url,
                      loadingPosition: 'start',
                      target: '_blank',
                  },
              },
              {
                  label: <FormattedMessage id="account.connections.dialog.nextStep" defaultMessage="Next Step" />,
                  buttonProps: {
                      variant: 'outlined',
                      color: 'neutral-800',
                      size: 'large',
                      sx: { borderColor: 'neutral-450.main' },
                      onClick: () => setCreateStep(2),
                  },
              },
          ]
        : isCreate
        ? [
              {
                  label: (
                      <FormattedMessage id="account.connections.dialog.connect" defaultMessage="Connect your account" />
                  ),
                  buttonProps: {
                      size: 'large',
                      loading: createLoading,
                      disabled: !dropboxCode.trim(),
                      onClick: handleCreateConfirm,
                  },
              },
              {
                  label: <FormattedMessage id="common.cancel.lowercase" defaultMessage="Cancel" />,
                  buttonProps: {
                      variant: 'outlined',
                      color: 'neutral-800',
                      size: 'large',
                      sx: { borderColor: 'neutral-450.main' },
                      onClick: () => setCreateStep(1),
                  },
              },
          ]
        : [
              {
                  label: (
                      <FormattedMessage
                          id="account.connections.dialog.disconnect"
                          defaultMessage="Disconnect your account"
                      />
                  ),
                  buttonProps: {
                      size: 'large',
                      loading: deleteLoading,
                      color: 'error',
                      onClick: handleDeleteConfirm,
                  },
              },
              {
                  label: <FormattedMessage id="common.cancel.lowercase" defaultMessage="Cancel" />,
                  buttonProps: {
                      variant: 'outlined',
                      color: 'neutral-800',
                      size: 'large',
                      sx: { borderColor: 'neutral-450.main' },
                      onClick: (event) => dialogProps.onClose?.(event, 'escapeKeyDown'),
                  },
              },
          ];

    return (
        <GenericActionDialog
            {...dialogProps}
            keepMounted
            icon={Icon}
            title={
                <FormattedMessage
                    id="account.connections.dialog.title"
                    defaultMessage="{folder} Connection"
                    values={{ folder: title }}
                />
            }
            content={content}
            buttons={buttons}
            viewKey={viewKey}
            onClose={handleDialogClose}
            onExited={onExited}
        />
    );
};
